import React, { useContext } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import { PapiUser } from '@wix/da-papi-types';
import { useThrottle } from '@wix/da-hooks/pkg/useThrottle';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { ButtonSize } from '@wix/da-ds/pkg/Buttons/types';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import SolidButton from '@wix/da-ds/pkg/Buttons/SolidButton';
import AddIcon from '@wix/da-ds/pkg/Icons/16x16/Add';
import Check from '@wix/da-ds/pkg/Icons/24x24/Check';
import Check16 from '@wix/da-ds/pkg/Icons/16x16/Check';
import useIconSizeForButtonSize from '@wix/da-ds/pkg/Buttons/hooks/useIconSizeForButtonSize';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { useRestrictToLoggedInAndVerified } from '../utils/hooks/useRestrictToLoggedInAndVerified';
import { ResourceType } from '../types/resource';
import {
  BiData,
  JoinGroupBiEvent,
  ViewGroupNotificationEvent,
} from '@wix/da-bi/pkg/events';

import s from './JoinGroupButton.scss';

export interface Props {
  group: PapiUser;
  size?: ButtonSize;
  className?: string;
  requestJoin: (group: PapiUser) => void;
  showAdminButton?: boolean;
  type?: 'text' | 'solid';
}

const JoinGroupButton: React.FC<Props> = ({
  group,
  requestJoin,
  className,
  showAdminButton,
  type = 'text',
  size,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const solidButtonIconSize = useIconSizeForButtonSize(size || 'medium');

  // throttle those button presses so there's time for any notifications to disappear/backend requests to complete
  const requestJoinThrottled = useThrottle(
    () => requestJoin(group),
    [requestJoin, group],
    6000
  );

  const { isLoggedIn, isVerificationNeeded, restrictToLoggedInAndVerified } =
    useRestrictToLoggedInAndVerified();

  const loggedInAndVerified = isLoggedIn && !isVerificationNeeded;
  const biData = loggedInAndVerified
    ? BiData<JoinGroupBiEvent>({
        evid: 381,
        groupid: group.useridUuid,
        sectionname: 'button',
      })
    : undefined;

  const onClick = restrictToLoggedInAndVerified(
    ReferralType.JOIN_GROUP,
    'joingroup',
    requestJoinThrottled
  );

  const { isAdmin, isIngroup } = group.extendedGroup || {};

  if (isAdmin) {
    if (showAdminButton) {
      return (
        <GhostButton
          size={size || 'medium'}
          variant="approval-green"
          className={className}
          href={Url.groupNotificationsLink(group.userId)}
          biData={BiData<ViewGroupNotificationEvent>({
            evid: 165,
            typeid: ResourceType.GROUP,
            itemid: group.userId,
            groupid: group.useridUuid,
            sectionname: 'button',
          })}
        >
          {t('joinGroupButton.admin')}
        </GhostButton>
      );
    }
    return (
      <GhostButton
        size={size || 'small'}
        variant="main"
        className={className}
        icon={Check}
        disabled
      >
        {group.extendedGroup?.rolelabel || 'Admin'}
      </GhostButton>
    );
  }
  if (isMobile) {
    if (isIngroup) {
      return (
        <GhostButton
          size={size || 'small'}
          variant="main"
          className={className}
          icon={Check}
          disabled
        >
          {t('joinGroupButton.member')}
        </GhostButton>
      );
    }

    return (
      <SolidButton
        variant="approval-green"
        size={size || 'small'}
        className={className}
        onClick={onClick}
        biData={biData}
      >
        {t('joinGroupButton.join')}
      </SolidButton>
    );
  }

  if (type === 'text') {
    if (isIngroup) {
      return (
        <TextButton
          size={size || 'small'}
          variant="main"
          className={className}
          icon={Check}
          iconClassName={s['check-icon']}
          disabled
          noHorizontalPaddings={false}
        >
          {t('joinGroupButton.member')}
        </TextButton>
      );
    }

    return (
      <TextButton
        size={size || 'small'}
        variant="approval-green"
        className={classnames(
          s['join-group-button'],
          s['join-text-btn'],
          className
        )}
        onClick={onClick}
        icon={AddIcon}
        iconClassName={s['plus-icon']}
        biData={BiData<JoinGroupBiEvent>({
          evid: 381,
          groupid: group.useridUuid,
        })}
        noHorizontalPaddings={false}
      >
        {t('joinGroupButton.join')}
      </TextButton>
    );
  }

  if (isIngroup) {
    const icon = solidButtonIconSize === IconSize.SMALLER ? Check16 : Check;
    return (
      <SolidButton
        size={size || 'medium'}
        variant="main"
        className={className}
        icon={icon}
        disabled
      >
        {t('joinGroupButton.member')}
      </SolidButton>
    );
  }
  return (
    <SolidButton
      size={size || 'medium'}
      variant="approval-green"
      className={className}
      onClick={onClick}
      biData={biData}
    >
      {t('joinGroupButton.join')}
    </SolidButton>
  );
};
JoinGroupButton.displayName = 'JoinGroupButton';

export default JoinGroupButton;
